/**
 * Error message library
 * Maps internal error codes from the GraphQL API to user-friendly messages
 * Add new codes here as needed — never expose internal error details to the user
 */

const ERROR_MESSAGES = {
  // Portal errors
  'POR-001': 'An unexpected error occurred. Please try again or contact support.',
  'POR-002': 'No results found for the given filters.',
  'POR-003': 'Your session has expired. Please log in again.',

  // User errors
  'USR-001': 'An error occurred with your user account. Please contact support.',
  'USR-004': 'This device is already registered to a user.',

  // Policy errors
  'PLC-001': 'An error occurred with the policy. Please try again or contact support.',

  // Device / SFC errors
  'SFC-001': 'An error occurred with the device. Please try again or contact support.',
  'SFC-002': 'An error occurred with the OTA update. Please try again or contact support.',

  // Logging errors
  'LOG-001': 'An error occurred with the bulk operation. Please try again or contact support.',
  'LOG-002': 'An error occurred with the job. Please try again or contact support.',

  // Whitelist errors
  'WL-001':  'An error occurred with the SSID whitelist. Please try again or contact support.',

  // Duplicate errors
  'DUP-001': 'An active session already exists for this user.',

  // HDM errors
  'HDM-001': 'An error occurred with the HDM operation. Please try again or contact support.',

  // Fallback
  'DEFAULT': 'An unexpected error occurred. Please try again or contact support.',
};

const NETWORK_ERROR_MESSAGE = 'A network error occurred. Please check your connection and try again.';

/**
 * Extracts the error code from a GraphQL error message
 * Handles formats like "USR-001 SOME_EXTENSION_CODE" or just "USR-001"
 * Returns null for anything that isn't a string (including null).
 * @param {string} message
 * @returns {string|null} the error code e.g. "USR-001"
 */
export const extractErrorCode = (message) => {
  if (typeof message !== 'string') return null;
  const match = message.match(/^([A-Z]+-\d+)/);
  return match ? match[1] : null;
};

/**
 * Returns a user-friendly message for a given error code
 * @param {string} code - e.g. "USR-001"
 * @returns {string}
 */
export const getMessageForCode = (code) => {
  return ERROR_MESSAGES[code] || ERROR_MESSAGES['DEFAULT'];
};

/**
 * Parses a GraphQL error and returns a user-friendly message
 * Works with a single GraphQL error object { message, extensions, ... }
 * @param {Object} error - a single GraphQL error object
 * @returns {string}
 */
export const parseGraphQLError = (error) => {
  if (!error?.message) return ERROR_MESSAGES['DEFAULT'];
  const code = extractErrorCode(error.message);
  return getMessageForCode(code);
};

/**
 * Parses an Apollo error and returns the first user-friendly message.
 *
 * Accepts every shape currently in the codebase:
 *   - Apollo v4 CombinedGraphQLErrors (`.errors` array)
 *   - useMutationCompat / useLazyQueryCompat results (`.errors` array)
 *   - Apollo v4 ServerError (GraphQL errors JSON in `.bodyText`, e.g. a 500 from
 *     the context function)
 *   - pre-v4 ApolloError (`.graphQLErrors` / `.networkError`)
 *   - any other Error instance, treated as a network-level failure
 *
 * TODO: drop the `.graphQLErrors` / `.networkError` branches once nothing
 * passes the pre-v4 shape.
 *
 * @param {Object} apolloError
 * @returns {string}
 */
export const parseApolloError = (apolloError) => {
  if (!apolloError) return ERROR_MESSAGES['DEFAULT'];

  // GraphQL errors: v4 / compat (.errors) or pre-v4 (.graphQLErrors)
  const graphQLErrors = apolloError.errors ?? apolloError.graphQLErrors;
  if (graphQLErrors?.length > 0) {
    return parseGraphQLError(graphQLErrors[0]);
  }

  // v4 ServerError: non-2xx response, GraphQL errors may be in the body
  if (typeof apolloError.bodyText === 'string') {
    try {
      const bodyErrors = JSON.parse(apolloError.bodyText)?.errors;
      if (bodyErrors?.length > 0) return parseGraphQLError(bodyErrors[0]);
    } catch {
      // body isn't JSON - fall through to the network message
    }
    return NETWORK_ERROR_MESSAGE;
  }

  // Network errors: pre-v4 shape, or a bare Error from v4
  if (apolloError.networkError || apolloError instanceof Error) {
    return NETWORK_ERROR_MESSAGE;
  }

  return ERROR_MESSAGES['DEFAULT'];
};

/**
 * Parses a raw GraphQL response body errors array
 * Works with fetch-based responses where you have the raw body
 * @param {Array} errors - the errors array from a raw GraphQL response body
 * @returns {string}
 */
export const parseResponseErrors = (errors = []) => {
  if (!errors?.length) return ERROR_MESSAGES['DEFAULT'];
  return parseGraphQLError(errors[0]);
};

export default {
  extractErrorCode,
  getMessageForCode,
  parseGraphQLError,
  parseApolloError,
  parseResponseErrors,
};
